import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InvoiceTable = ({ invoices, onInvoiceSelect, onStatusChange, onPaymentRecord }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  const getStatusConfig = (status) => {
    const configs = {
      draft: { label: 'Borrador', color: 'bg-secondary-100 text-secondary-600', icon: 'FileText' },
      sent: { label: 'Enviada', color: 'bg-primary-100 text-primary-600', icon: 'Send' },
      paid: { label: 'Pagada', color: 'bg-success-100 text-success-600', icon: 'CheckCircle' },
      overdue: { label: 'Vencida', color: 'bg-error-100 text-error-600', icon: 'AlertCircle' },
      cancelled: { label: 'Cancelada', color: 'bg-secondary-100 text-secondary-600', icon: 'X' }
    };
    return configs[status] || configs.draft;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedInvoices(invoices.map(invoice => invoice.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(id => id !== invoiceId));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES');
  };

  const getDaysOverdue = (dueDate, status) => {
    if (status !== 'overdue') return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    if (sortConfig.key === 'amount') {
      return sortConfig.direction === 'asc' 
        ? a.amount - b.amount 
        : b.amount - a.amount;
    }
    
    if (sortConfig.key === 'date' || sortConfig.key === 'dueDate') {
      const aDate = new Date(a[sortConfig.key]);
      const bDate = new Date(b[sortConfig.key]);
      return sortConfig.direction === 'asc' 
        ? aDate - bDate 
        : bDate - aDate;
    }
    
    const aValue = a[sortConfig.key]?.toString().toLowerCase() || '';
    const bValue = b[sortConfig.key]?.toString().toLowerCase() || '';
    
    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue);
    }
    return bValue.localeCompare(aValue);
  });

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <Icon name="ArrowUpDown" size={16} className="text-text-muted" />;
    }
    return (
      <Icon 
        name={sortConfig.direction === 'asc' ? 'ArrowUp' : 'ArrowDown'} 
        size={16} 
        className="text-primary" 
      />
    );
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Table Header */}
      <div className="px-6 py-4 border-b border-border bg-secondary-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-heading-semibold text-text-primary">
            Lista de Facturas
          </h3>
          
          {selectedInvoices.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-secondary">
                {selectedInvoices.length} seleccionadas
              </span>
              <Button
                variant="primary"
                size="sm"
                iconName="Send"
                iconSize={16}
              >
                Enviar por Email
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconName="Download"
                iconSize={16}
              >
                Descargar PDF
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedInvoices.length === invoices.length && invoices.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-input"
                />
              </th>
              
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('number')}
                  className="flex items-center space-x-2 text-sm font-body-medium text-text-primary hover:text-primary"
                >
                  <span>Número</span>
                  <SortIcon column="number" />
                </button>
              </th>
              
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('client')}
                  className="flex items-center space-x-2 text-sm font-body-medium text-text-primary hover:text-primary"
                >
                  <span>Cliente</span>
                  <SortIcon column="client" />
                </button>
              </th>
              
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-2 text-sm font-body-medium text-text-primary hover:text-primary"
                >
                  <span>Fecha</span>
                  <SortIcon column="date" />
                </button>
              </th>
              
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-2 text-sm font-body-medium text-text-primary hover:text-primary"
                >
                  <span>Importe</span>
                  <SortIcon column="amount" />
                </button>
              </th>
              
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 text-sm font-body-medium text-text-primary hover:text-primary"
                >
                  <span>Estado</span>
                  <SortIcon column="status" />
                </button>
              </th>
              
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('dueDate')}
                  className="flex items-center space-x-2 text-sm font-body-medium text-text-primary hover:text-primary"
                >
                  <span>Vencimiento</span>
                  <SortIcon column="dueDate" />
                </button>
              </th>
              
              <th className="px-6 py-3 text-right">
                <span className="text-sm font-body-medium text-text-primary">Acciones</span>
              </th>
            </tr>
          </thead>
          
          <tbody className="divide-y divide-border">
            {sortedInvoices.map((invoice) => {
              const statusConfig = getStatusConfig(invoice.status);
              const daysOverdue = getDaysOverdue(invoice.dueDate, invoice.status);
              
              return (
                <tr key={invoice.id} className="hover:bg-secondary-50 transition-micro">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                      className="rounded border-input"
                    />
                  </td>
                  
                  <td className="px-6 py-4">
                    <button
                      onClick={() => onInvoiceSelect(invoice)}
                      className="text-primary hover:text-primary-700 font-body-medium"
                    >
                      {invoice.number}
                    </button>
                  </td>
                  
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-body-medium text-text-primary">{invoice.client}</div>
                      <div className="text-sm text-text-secondary">{invoice.clientEmail}</div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 text-text-secondary">
                    {formatDate(invoice.date)}
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="font-body-medium text-text-primary">
                      {formatCurrency(invoice.amount)}
                    </div>
                    {invoice.paidAmount > 0 && invoice.paidAmount < invoice.amount && (
                      <div className="text-sm text-success">
                        Pagado: {formatCurrency(invoice.paidAmount)}
                      </div>
                    )}
                  </td>
                  
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-body-medium ${statusConfig.color}`}>
                        <Icon name={statusConfig.icon} size={12} className="mr-1" />
                        {statusConfig.label}
                      </span>
                      {daysOverdue && (
                        <span className="text-xs text-error font-body-medium">
                          +{daysOverdue} días
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 text-text-secondary">
                    <div>
                      {formatDate(invoice.dueDate)}
                      {invoice.status === 'overdue' && (
                        <div className="text-xs text-error">Vencida</div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        iconSize={16}
                        onClick={() => onInvoiceSelect(invoice)}
                      />
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        iconSize={16}
                      />
                      
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="CreditCard"
                          iconSize={16}
                          onClick={() => onPaymentRecord(invoice)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card Layout */}
      <div className="lg:hidden divide-y divide-border">
        {sortedInvoices.map((invoice) => {
          const statusConfig = getStatusConfig(invoice.status);
          const daysOverdue = getDaysOverdue(invoice.dueDate, invoice.status);
          
          return (
            <div key={invoice.id} className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                    className="rounded border-input"
                  />
                  <div>
                    <button
                      onClick={() => onInvoiceSelect(invoice)}
                      className="text-primary hover:text-primary-700 font-body-medium"
                    >
                      {invoice.number}
                    </button>
                    <div className="text-sm text-text-secondary">{invoice.client}</div>
                  </div>
                </div>
                
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-body-medium ${statusConfig.color}`}>
                  <Icon name={statusConfig.icon} size={12} className="mr-1" />
                  {statusConfig.label}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-sm text-text-secondary">Importe</div>
                  <div className="font-body-medium text-text-primary">
                    {formatCurrency(invoice.amount)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-text-secondary">Vencimiento</div>
                  <div className="text-sm text-text-primary">
                    {formatDate(invoice.dueDate)}
                    {daysOverdue && (
                      <span className="text-error ml-1">+{daysOverdue}d</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary">
                  {formatDate(invoice.date)}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    iconSize={16}
                    onClick={() => onInvoiceSelect(invoice)}
                  />
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Download"
                    iconSize={16}
                  />
                  
                  {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="CreditCard"
                      iconSize={16}
                      onClick={() => onPaymentRecord(invoice)}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {invoices.length === 0 && (
        <div className="px-6 py-12 text-center">
          <Icon name="FileText" size={48} className="text-text-muted mx-auto mb-4" />
          <h3 className="text-lg font-heading-semibold text-text-primary mb-2">
            No hay facturas
          </h3>
          <p className="text-text-secondary mb-4">
            Comienza creando tu primera factura o convirtiendo un presupuesto.
          </p>
          <Button variant="primary" iconName="Plus" iconSize={16}>
            Nueva Factura
          </Button>
        </div>
      )}
    </div>
  );
};

export default InvoiceTable;